/**
 * API Client for Web Frontend
 *
 * This module provides a centralized API client for making HTTP requests to the backend.
 * It handles authentication, request formatting, error handling, and response parsing.
 * Includes simple request deduplication and caching for better performance.
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'auth_token'; // Must match AuthContext.tsx
const USER_KEY = 'user'; // Must match AuthContext.tsx
const SESSION_KEY = 'session'; // Must match AuthContext.tsx

// Guard to prevent multiple reload attempts
let isReloadingDueToAuth401 = false;

// Simple request cache for GET requests
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 60 * 1000; // 1 minute cache

// In-flight requests to deduplicate concurrent requests
const inFlightRequests = new Map<string, Promise<any>>();

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

async function apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;

  // Build URL - use BACKEND_URL if provided, otherwise use relative path (works on same origin)
  let url: string;
  if (BACKEND_URL) {
    url = `${BACKEND_URL}/api${endpoint}`;
  } else {
    url = `/api${endpoint}`;
  }

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Get auth token from localStorage
  const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
  console.log(`[Web API] ${fetchOptions.method || 'GET'} ${url} - Token: ${authToken ? 'present' : 'missing'}`);

  // Add Authorization header if token exists
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  console.log(`[Web API] Response status: ${response.status} ${response.statusText}`);

  if (!response.ok) {
    // Try to parse error response first
    let errorMessage = `API Error: ${response.status}`;
    let responseData: any = null;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        errorMessage = responseData.message || responseData.error || errorMessage;
      }
    } catch (e) {
      // Ignore parse errors, will handle below
    }

    // If user is locked (403 with lock-specific error), update user state with locked flag
    // NOTE: Only treat 403 as "locked" if error message explicitly mentions account lock
    // General permission denials also return 403 and should not lock the account
    if (response.status === 403 && errorMessage && errorMessage.toLowerCase().includes('lock')) {
      console.warn("[Web API] 403 FORBIDDEN - User account is locked");

      // Only dispatch the event if the user is not already marked as locked
      const storedUserStr = localStorage.getItem(USER_KEY);
      const isAlreadyLocked = storedUserStr ? JSON.parse(storedUserStr).isLocked === true : false;

      // If we have user data from the error response, use it
      if (responseData?.user) {
        console.log('[Web API] Updating user with locked status from error response');
        const lockedUser = { ...responseData.user, isLocked: true };
        localStorage.setItem(USER_KEY, JSON.stringify(lockedUser));

        // Only dispatch event if user wasn't already locked (to avoid spam)
        if (!isAlreadyLocked) {
          window.dispatchEvent(new CustomEvent('userLocked', { detail: lockedUser }));
        }
      } else if (!isAlreadyLocked) {
        // If no user data in response and not already locked, mark current user as locked
        if (storedUserStr) {
          try {
            const storedUser = JSON.parse(storedUserStr);
            const lockedUser = { ...storedUser, isLocked: true };
            localStorage.setItem(USER_KEY, JSON.stringify(lockedUser));

            // Dispatch custom event so AuthContext can pick up the change
            window.dispatchEvent(new CustomEvent('userLocked', { detail: lockedUser }));
          } catch (e) {
            console.error('[Web API] Failed to parse stored user:', e);
          }
        }
      }

      // Create error with data attached
      const error = new Error(errorMessage) as any;
      error.status = response.status;
      error.response = response;
      error.data = responseData;
      throw error;
    } else if (response.status === 403) {
      // For other 403 errors (permission denial), log but don't lock account
      console.warn("[Web API] 403 FORBIDDEN - Permission denied:", errorMessage);

      // Create error with data attached
      const error = new Error(errorMessage) as any;
      error.status = response.status;
      error.response = response;
      error.data = responseData;
      throw error;
    }

    // If user is unauthorized (401), clear auth and redirect to login
    if (response.status === 401) {
      console.warn("[Web API] 401 UNAUTHORIZED - User is no longer authenticated");

      // Clear the auth token immediately
      api.setAuthToken(null);

      // Also clear user data from localStorage
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(SESSION_KEY);

      // Clear Supabase session from localStorage without calling Supabase API
      // (calling signOut() has CORS issues in web context)
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-') || key.startsWith('supabase')) {
          console.warn(`[Web API] Clearing Supabase key: ${key}`);
          localStorage.removeItem(key);
        }
      });

      // Only redirect once - prevent multiple redirects from concurrent requests
      if (!isReloadingDueToAuth401) {
        isReloadingDueToAuth401 = true;
        console.warn("[Web API] Redirecting to login");
        // Hard redirect to login page
        window.location.href = '/login';
      }

      // Don't throw error - let the redirect happen
      return new Promise(() => {}); // Return pending promise to prevent further execution
    }

    // For other errors, parse response and throw
    if (!errorMessage || errorMessage === `API Error: ${response.status}`) {
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // Already parsed above
        } else {
          const text = await response.text();
          console.error(`[Web API] Non-JSON error response:`, text.substring(0, 200));
          errorMessage = `API Error: ${response.status} - Non-JSON response`;
        }
      } catch (e) {
        console.error(`[Web API] Error parsing response:`, e);
      }
    }

    // Create error with status code attached
    const error = new Error(errorMessage) as any;
    error.status = response.status;
    error.response = response;
    error.data = responseData;
    throw error;
  }

  // Handle JSON parsing with better error handling
  try {
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error(`[Web API] Expected JSON but got:`, text.substring(0, 200));
      throw new Error('Server returned non-JSON response');
    }
    return response.json();
  } catch (e) {
    console.error(`[Web API] JSON parse error:`, e);
    throw e;
  }
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  put: <T>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),

  setAuthToken: (token: string | null) => {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  },
  clearCache: () => {
    requestCache.clear();
    console.log('[Web API] Cache cleared');
  },
  invalidateEndpoint: (endpoint: string) => {
    const keysToDelete: string[] = [];
    requestCache.forEach((_, key) => {
      if (key.includes(endpoint)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => requestCache.delete(key));
    console.log(`[Web API] Invalidated ${keysToDelete.length} cache entries for ${endpoint}`);
  },
};
